import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, Tag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/posts?limit=50`)
      .then(res => setPosts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === "all" ? posts : posts.filter(p => p.category === category);
  const cats = Object.entries(t.blog.categories);

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title="Aktuality a novinky"
        description="Nejnovější zprávy z kapely Rebelky – backstage z koncertů, ohlášení nových písniček, rozhovory a fotogalerie z Bezvafest tour 2026. Sleduj náš blog a buď u všeho první."
        keywords="Rebelky novinky, Rebelky blog, kapela aktuality, backstage Rebelky, Bezvafest 2026"
        url="/aktuality"
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <section style={{ padding: "4rem 1.5rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-7xl mx-auto">
            <p className="section-label mb-3">{t.blog.subtitle}</p>
            <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 6vw, 4rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.03em" }}>
              {t.blog.title}
            </h1>
            {/* Category filter */}
            <div className="flex gap-2 mt-6 flex-wrap" data-testid="blog-category-filter">
              {cats.map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`blog-filter-${key}`}
                  onClick={() => setCategory(key)}
                  style={{
                    background: category === key ? "#FF005A" : "transparent",
                    border: "1px solid " + (category === key ? "#FF005A" : "rgba(255,255,255,0.1)"),
                    color: "white",
                    padding: "0.4rem 1rem",
                    fontFamily: "Unbounded, sans-serif",
                    fontSize: "0.6rem",
                    letterSpacing: "0.1em",
                    cursor: "pointer",
                    borderRadius: "4px",
                    transition: "all 0.2s",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <p style={{ color: "#52525B", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.2em" }}>{t.common.loading}</p>
          ) : filtered.length === 0 ? (
            <p style={{ color: "#52525B" }}>{t.common.noResults}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <motion.div
                  key={post._id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`post-card-${i}`}
                  className="card-hover"
                  style={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden" }}
                >
                  {post.image_url && (
                    <div style={{ aspectRatio: "16/9", overflow: "hidden" }}>
                      <img
                        src={post.image_url}
                        alt={lang === "en" && post.title_en ? post.title_en : post.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                        onError={e => { e.target.style.display = "none"; }}
                      />
                    </div>
                  )}
                  <div style={{ padding: "1.5rem" }}>
                    <div className="flex items-center gap-3 mb-3">
                      <span style={{ background: "rgba(255,0,90,0.1)", border: "1px solid rgba(255,0,90,0.3)", color: "#FF005A", padding: "0.2rem 0.6rem", fontFamily: "Unbounded, sans-serif", fontSize: "0.5rem", letterSpacing: "0.15em", borderRadius: "2px" }}>
                        {t.blog.categories[post.category] || post.category}
                      </span>
                      <div className="flex items-center gap-1">
                        <Calendar size={11} style={{ color: "#52525B" }} />
                        <span style={{ color: "#52525B", fontSize: "0.75rem" }}>
                          {new Date(post.published_at).toLocaleDateString(lang === "cz" ? "cs-CZ" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </div>
                    </div>
                    <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#FFFFFF", textTransform: "uppercase", lineHeight: 1.4, marginBottom: "0.8rem" }} className="line-clamp-2">
                      {lang === "en" && post.title_en ? post.title_en : post.title}
                    </h2>
                    {(post.excerpt || post.excerpt_en) && (
                      <p style={{ color: "#A1A1AA", fontSize: "0.85rem", lineHeight: 1.6, marginBottom: "1rem" }} className="line-clamp-3">
                        {lang === "en" && post.excerpt_en ? post.excerpt_en : post.excerpt}
                      </p>
                    )}
                    <Link
                      to={`/aktuality/${post.slug}`}
                      data-testid={`read-more-${i}`}
                      style={{ color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.15em", textTransform: "uppercase", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px" }}
                      className="hover:text-white transition-colors"
                    >
                      {t.blog.readMore} →
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
